/**
 * useFacilities — Iter78 (Batch D)
 *
 * Fetches emergency facilities (hospitals / police / fire / fama /
 * embassies) from the new `/api/facilities` endpoint, backed by the
 * MongoDB `facilities` collection (seeded from HDX + OSM Overpass +
 * Google Places, admin overrides).
 *
 * Sep 2026 — data-consistency hotfix: this hook used to initialize its
 * state SYNCHRONOUSLY to the legacy hardcoded `EMERGENCY_SERVICES` list
 * (old/uncorrected coordinates) on every mount, then swap in the live
 * `/api/facilities` data once the fetch resolved. On a full page reload,
 * that meant every user briefly saw the WRONG marker position before it
 * "jumped" to the corrected one a moment later — and depending on
 * network timing, a screenshot/observation could catch either state,
 * looking like the marker "alternates" between two coordinates (see
 * "Hôpital du Mali" investigation, Sep 2026). The live API response is
 * now the ONLY source of truth on a normal load; `EMERGENCY_SERVICES` is
 * used ONLY as an explicit OFFLINE fallback (network fetch genuinely
 * failed) — never as a transient default that gets silently overwritten.
 */
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { EMERGENCY_SERVICES } from "../data/maliEmergencyServices";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

// Cache the last successful fetch in-memory so map remounts don't refetch.
let _cache = null;
let _cacheAt = 0;
const CACHE_TTL_MS = 5 * 60 * 1000; // 5 min

// Opt-in diagnostic logging — set `localStorage.rr_poi_debug = "1"` in
// devtools to see, for every load, which source/version served the POIs
// and how long it took. Off by default, zero overhead for normal users.
const poiDebug = (label, info) => {
  try {
    if (localStorage.getItem("rr_poi_debug") === "1") {
      // eslint-disable-next-line no-console
      console.info(`[useFacilities] ${label}`, { ...info, at: new Date().toISOString() });
    }
  } catch (_e) { /* noop */ }
};

const _mapLegacy = (row) => ({
  id: row.id,
  type: row.category,
  name: row.name,
  lat: row.lat,
  lng: row.lng,
  phone: row.phone || "",
  city: row.city || "",
  country: "ML",
  source: "static_fallback",
  verified: true,
});

export const useFacilities = ({ countries = "ML,GN" } = {}) => {
  // Only ever start from a WARM cache of a real API response. Never
  // default to the static legacy list here — that was the source of the
  // "marker jumps after refresh" bug. `items` starts empty and is filled
  // once, from exactly one source (see effect below).
  const [items, setItems] = useState(_cache || []);
  const [loading, setLoading] = useState(!_cache);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;

    // Serve from cache if fresh
    if (_cache && Date.now() - _cacheAt < CACHE_TTL_MS) {
      setItems(_cache);
      setLoading(false);
      poiDebug("served-from-memory-cache", { count: _cache.length, cacheAgeMs: Date.now() - _cacheAt });
      return () => { cancelled = true; };
    }

    (async () => {
      setLoading(true);
      const startedAt = Date.now();
      try {
        const url = `${API_BASE}/api/facilities?country=${encodeURIComponent(countries)}&limit=3000`;
        const r = await axios.get(url, { timeout: 8000 });
        const list = Array.isArray(r?.data?.items) ? r.data.items : [];
        if (list.length === 0) {
          // Backend online but empty — probably seed hasn't run yet. Keep the
          // hardcoded fallback so citizens still see the core Bamako map.
          const fallback = EMERGENCY_SERVICES.map(_mapLegacy);
          if (!cancelled) setItems(fallback);
          poiDebug("live-api-empty-using-static-fallback", { tookMs: Date.now() - startedAt });
        } else if (!cancelled) {
          setItems(list);
          _cache = list;
          _cacheAt = Date.now();
          poiDebug("live-api-success", {
            count: list.length,
            tookMs: Date.now() - startedAt,
            sources: list.reduce((acc, it) => { acc[it.source] = (acc[it.source] || 0) + 1; return acc; }, {}),
          });
        }
        if (!cancelled) setError(null);
      } catch (e) {
        console.warn("[useFacilities] fetch failed, using OFFLINE fallback:", e?.message || e);
        if (!cancelled) {
          setError(e);
          setItems(EMERGENCY_SERVICES.map(_mapLegacy));
          poiDebug("live-api-failed-using-offline-fallback", { tookMs: Date.now() - startedAt, error: e?.message });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [countries]);

  const byType = useMemo(() => {
    const out = { hospital: 0, police: 0, fire: 0, fama: 0, embassy: 0 };
    items.forEach((it) => {
      const t = it.type;
      if (t in out) out[t] += 1;
    });
    return out;
  }, [items]);

  return { items, loading, error, byType };
};
